import React, { useState, useEffect, useRef, useMemo, Fragment } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaSearch,
} from "react-icons/fa";
import { Dialog, Transition } from "@headlessui/react";
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
// Import RTK Query hooks
import { useGetPlatformIssuesByScanIdQuery } from "../../app/api/platform/platformIssuesApiSlice";
import { useGetConsoleIssuesByScanIdQuery } from "../../app/api/console/consoleScansApiSlice";
import { useGetFindingStatusesByScanIdQuery } from "../../app/api/console/platformFindingsApiSlice";
import { useDeletePlatformIssueMutation } from "../../app/api/platform/platformIssuesApiSlice";
import { useDeleteConsoleIssueMutation } from "../../app/api/console/consoleIssuesApiSlice";
import { ENVIRONMENTS } from "../../pages/common/ScansByEnvironment";
import { runBulkIssueDeletes } from "../../app/utils/bulkDeleteIssues";
import { formatMutationError } from "../../app/utils/mutationError";
// For console issues, we'll use the import from ScansByEnvironment since consoleIssuesApiSlice doesn't have the right endpoint

const ScanListItem = ({
  scan,
  isSelected,
  onSelect,
  onDeleteIssues,
  onExpand,
  onTogglePushSelect,
  onIssueSelectionChange,
  selectedForPush = false,
  environment,
  onDeleteAllIssuesByStatus,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedIssueIds, setSelectedIssueIds] = useState([]);
  const [localIssueCount, setLocalIssueCount] = useState(0);

  // Ref to track the previous value of selectedForPush
  // This lets us detect when it changes from false to true
  const prevSelectedForPushRef = useRef(selectedForPush);

  // Ref to track if user has manually changed selection (to cancel pending auto-selections)
  const userChangedSelectionRef = useRef(false);

  // State for search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // State for delete confirmation and progress
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({
    current: 0,
    total: 0,
    success: 0,
    failed: 0,
    isComplete: false,
  });
  const [deleteError, setDeleteError] = useState(null);

  // Determine if we're using platform API (vs console API)
  const isPlatform = environment !== ENVIRONMENTS.PRE_STAGING;
  const useProd = environment === ENVIRONMENTS.PRODUCTION;
  const isActivityRunOnly = Boolean(scan?.activity_run_only);

  // Get the appropriate scan ID for queries
  const scanId = scan.id || scan.scan_id || scan.console_scan_id;

  // Deletion mutations
  const [deletePlatformIssue] = useDeletePlatformIssueMutation();
  const [deleteConsoleIssue] = useDeleteConsoleIssueMutation();
  const {
    data: platformIssues = [],
    isLoading: isLoadingPlatformIssues,
    isFetching: isFetchingPlatformIssues,
  } = useGetPlatformIssuesByScanIdQuery(
    { scanId, useProd },
    {
      skip: !isPlatform || !isExpanded || !scanId || isActivityRunOnly,
    }
  );

  // Query console issues with skip unless expanded
  const {
    data: consoleIssues = [],
    isLoading: isLoadingConsoleIssues,
    isFetching: isFetchingConsoleIssues,
  } = useGetConsoleIssuesByScanIdQuery(scanId, {
    skip: isPlatform || !isExpanded || !scanId || isActivityRunOnly,
  });

  // Discards/resolutions live in the platform V2 findings table, not the V1
  // issue rows fetched above. Pull the finding-status overlay and apply it so
  // discarded findings group correctly instead of all showing as "open".
  const { data: findingStatusData } = useGetFindingStatusesByScanIdQuery(
    { scanId, env: useProd ? "prod" : "staging" },
    { skip: !isPlatform || !isExpanded || !scanId || isActivityRunOnly }
  );

  // Get the actual issues based on API being used
  const issues = useMemo(() => {
    const base = isPlatform ? platformIssues : consoleIssues;
    const statuses = findingStatusData?.statuses;
    if (!isPlatform || !statuses || !Array.isArray(base) || base.length === 0) {
      return base;
    }
    return base.map((issue) => {
      const overlay = statuses[issue.issue_id || issue.id];
      if (!overlay) return issue;
      return {
        ...issue,
        status: overlay,
        visible_issue_content: {
          ...(issue.visible_issue_content || {}),
          status: overlay,
        },
      };
    });
  }, [isPlatform, platformIssues, consoleIssues, findingStatusData]);
  const isLoadingIssues = isPlatform
    ? isLoadingPlatformIssues || isFetchingPlatformIssues
    : isLoadingConsoleIssues || isFetchingConsoleIssues;

  // Get issue count
  const getIssueCount = () => {
    // Check all possible issue locations in the scan object
    if (scan.issues_ids && Array.isArray(scan.issues_ids)) {
      return scan.issues_ids.length;
    }
    if (scan.issues_count !== undefined) {
      return scan.issues_count;
    }
    if (scan.console_issue_ids && Array.isArray(scan.console_issue_ids)) {
      return scan.console_issue_ids.length;
    }
    return 0;
  };

  // Use either real-time count or stored count
  const displayIssueCount = issues.length > 0 ? issues.length : localIssueCount;

  // Calculate and store issue count when scan changes
  useEffect(() => {
    setLocalIssueCount(getIssueCount());
    // `getIssueCount` is a render-scoped helper that only reads `scan`; this effect is
    // meant to run when `scan` changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan]);

  // Reset selected issues when scan or expansion changes
  useEffect(() => {
    setSelectedIssueIds([]);
  }, [scanId]);

  // Auto-select issues based on search query
  useEffect(() => {
    if (searchQuery && issues && issues.length > 0) {
      const matchingIssues = issues.filter((issue) =>
        getIssueTitle(issue).toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchingIds = matchingIssues.map((issue) => getIssueId(issue));
      setSelectedIssueIds(matchingIds);
      onIssueSelectionChange && onIssueSelectionChange(scanId, matchingIssues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, issues]);

  // Handle selectedForPush changes - only auto-select when checkbox is first checked
  // This effect ONLY depends on selectedForPush, NOT on issues
  useEffect(() => {
    const wasSelectedForPush = prevSelectedForPushRef.current;
    prevSelectedForPushRef.current = selectedForPush;

    // Only act when selectedForPush changes from false to true (checkbox just checked)
    if (selectedForPush && !wasSelectedForPush) {
      // Check if user already manually selected issues BEFORE resetting the flag
      // This happens when user clicks "Select" on a status group, which triggers
      // selectedForPush to become true via onIssueSelectionChange
      if (userChangedSelectionRef.current) {
        // User already made a selection - don't override it
        return;
      }
      // Auto-select open issues if already loaded
      if (issues && issues.length > 0) {
        const openIssues = getOpenIssues();
        if (openIssues.length > 0) {
          const issueIds = openIssues.map((issue) => getIssueId(issue));
          setSelectedIssueIds(issueIds);
          onIssueSelectionChange && onIssueSelectionChange(scanId, openIssues);
        }
      }
      // If issues aren't loaded yet, handleScanPushCheckbox will handle it
    } else if (!selectedForPush && wasSelectedForPush) {
      // Checkbox was unchecked - clear selection and reset flag
      userChangedSelectionRef.current = false;
      setSelectedIssueIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedForPush]);

  // Toggle expansion state
  const toggleExpand = (e) => {
    e.stopPropagation();
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);

    // Still call onExpand for compatibility with parent component
    if (newExpandedState && onExpand) {
      onExpand(scan);
    }
  };

  const toggleIssueSelection = (issueId) => {
    // Mark that user has manually changed selection (cancels pending auto-selections)
    userChangedSelectionRef.current = true;
    setSelectedIssueIds((prev) => {
      const next = prev.includes(issueId)
        ? prev.filter((id) => id !== issueId)
        : [...prev, issueId];
      onIssueSelectionChange &&
        onIssueSelectionChange(
          scanId,
          issues.filter((iss) => next.includes(getIssueId(iss)))
        );
      return next;
    });
  };

  const selectAllIssues = () => {
    if (!issues || issues.length === 0) return;
    // Mark that user has manually changed selection
    userChangedSelectionRef.current = true;
    const issueIds = issues.map((issue) => getIssueId(issue));
    setSelectedIssueIds(issueIds);
    onIssueSelectionChange && onIssueSelectionChange(scanId, issues);
  };

  // Helper function to get only open issues
  const getOpenIssues = () => {
    if (!issues || issues.length === 0) return [];
    return issues.filter((issue) => {
      const status =
        issue.status ||
        issue.visible_issue_content?.status ||
        issue.created_issue_content?.status ||
        issue.issue_content?.status ||
        "open"; // Default to "open" if no status found
      return status === "open";
    });
  };

  const selectOpenIssues = () => {
    // Mark that user has manually changed selection
    userChangedSelectionRef.current = true;
    const openIssues = getOpenIssues();
    if (openIssues.length === 0) return;
    const issueIds = openIssues.map((issue) => getIssueId(issue));
    setSelectedIssueIds(issueIds);
    onIssueSelectionChange && onIssueSelectionChange(scanId, openIssues);
  };

  // Toggle selection for a status group (add to or remove from current selection)
  const toggleStatusGroupSelection = (targetStatus, statusIssues) => {
    // Mark that user has manually changed selection (cancels pending auto-selections)
    userChangedSelectionRef.current = true;
    const statusIssueIds = statusIssues.map((issue) => getIssueId(issue));
    const allStatusSelected = statusIssueIds.every((id) =>
      selectedIssueIds.includes(id)
    );

    let newSelectedIds;
    if (allStatusSelected) {
      // Deselect all issues in this status group
      newSelectedIds = selectedIssueIds.filter(
        (id) => !statusIssueIds.includes(id)
      );
    } else {
      // Add all issues in this status group to selection
      newSelectedIds = [
        ...new Set([...selectedIssueIds, ...statusIssueIds]),
      ];
    }

    setSelectedIssueIds(newSelectedIds);
    onIssueSelectionChange &&
      onIssueSelectionChange(
        scanId,
        issues.filter((iss) => newSelectedIds.includes(getIssueId(iss)))
      );
  };

  // Check if all issues in a status group are selected
  const areAllStatusIssuesSelected = (statusIssues) => {
    if (!statusIssues || statusIssues.length === 0) return false;
    return statusIssues.every((issue) =>
      selectedIssueIds.includes(getIssueId(issue))
    );
  };

  // Clear all selected issues
  const clearIssueSelection = () => {
    setSelectedIssueIds([]);
    setSearchQuery("");
    onIssueSelectionChange && onIssueSelectionChange(scanId, []);
  };

  // Check if any issues are selected
  const hasSelectedIssues = () => {
    return selectedIssueIds.length > 0;
  };

  // Check if all issues are selected
  const areAllIssuesSelected = () => {
    return (
      issues && issues.length > 0 && selectedIssueIds.length === issues.length
    );
  };

  // Group issues by status
  const groupIssuesByStatus = () => {
    if (!issues || issues.length === 0) return {};

    const statusOrder = ["open", "closed", "discarded"];

    // Group issues by status
    const grouped = issues.reduce((acc, issue) => {
      // Get the status from various possible locations
      const status =
        issue.status ||
        issue.visible_issue_content?.status ||
        issue.created_issue_content?.status ||
        issue.issue_content?.status ||
        "open"; // Default to "open" if no status found

      if (!acc[status]) acc[status] = [];
      acc[status].push(issue);
      return acc;
    }, {});

    // Create ordered result
    const result = [];

    // Add ordered statuses first
    statusOrder.forEach((status) => {
      if (grouped[status] && grouped[status].length > 0) {
        result.push({
          status,
          issues: grouped[status],
        });
      }
    });

    // Add any remaining statuses not in the order
    Object.keys(grouped).forEach((status) => {
      if (!statusOrder.includes(status) && grouped[status].length > 0) {
        result.push({
          status,
          issues: grouped[status],
        });
      }
    });

    return result;
  };

  // Format status for display
  const formatStatusTitle = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  // Get status styling
  const getStatusStyling = (status) => {
    switch (status) {
      case "open":
        return "text-accent-purple bg-accent-purple/5 border-accent-purple/20";
      case "closed":
        return "text-green-700 bg-green-50 border-green-200";
      case "discarded":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-text-secondary bg-surface-neutral border-border-subtle";
    }
  };

  // Show delete confirmation modal
  const handleDeleteClick = () => {
    if (selectedIssueIds.length > 0) {
      setShowDeleteModal(true);
      setDeleteError(null);
      setDeleteProgress({
        current: 0,
        total: 0,
        success: 0,
        failed: 0,
        isComplete: false,
      });
    }
  };

  // Delete selected issues with progress tracking
  const handleDeleteSelectedIssues = async () => {
    if (selectedIssueIds.length === 0) return;

    if (!scanId) {
      console.error("Can't delete issues: No scan ID found", scan);
      setDeleteError("No scan ID found");
      return;
    }

    setDeleteInProgress(true);
    setDeleteProgress({
      current: 0,
      total: selectedIssueIds.length,
      success: 0,
      failed: 0,
      isComplete: false,
    });

    let successCount = 0;
    let failedCount = 0;
    const successfulIssueIds = [];

    try {
      const result = await runBulkIssueDeletes({
        issues: selectedIssueIds.map((issueId) => ({ issue_id: issueId })),
        getIssueId: (issue) => issue.issue_id,
        deleteOne: async (issueId) => {
          if (isPlatform) {
            await deletePlatformIssue({ issueId, useProd }).unwrap();
          } else {
            await deleteConsoleIssue(issueId).unwrap();
          }
        },
        onProgress: setDeleteProgress,
      });
      successCount = result.successCount;
      failedCount = result.failedCount;
      successfulIssueIds.push(...result.successfulIssueIds);

      // Call the onDeleteIssues callback to refresh the parent component (only for successful deletions)
      if (onDeleteIssues && successfulIssueIds.length > 0) {
        onDeleteIssues(scanId, successfulIssueIds);
      }

      // If all were successful, close modal after a brief delay
      if (failedCount === 0) {
        setTimeout(() => {
          clearIssueSelection();
          setShowDeleteModal(false);
          setDeleteInProgress(false);
        }, 1500);
      } else {
        // If there were failures, show error but don't auto-close
        setDeleteError(
          result.errorMessage ||
            `Completed with ${successCount} successful and ${failedCount} failed deletions`
        );
        setDeleteInProgress(false);
      }
    } catch (error) {
      console.error("Failed to delete issues:", error);
      setDeleteError(formatMutationError(error, "Failed to delete issues"));
      setDeleteProgress((prev) => ({
        ...prev,
        failed: selectedIssueIds.length - prev.success,
        isComplete: true,
      }));
      setDeleteInProgress(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Function to safely handle selection
  const handleSelect = () => {
    if (onSelect) {
      // Ensure that scan has both ID fields
      const scanWithIds = {
        ...scan,
        id: scan.id || scan.scan_id,
        scan_id: scan.scan_id || scan.id,
      };
      onSelect(scanWithIds);
    }
  };

  // Helper to get the issue ID regardless of schema
  const getIssueId = (issue) => {
    // Platform API uses issue_id
    if (issue.issue_id) {
      return issue.issue_id;
    }
    // Console API uses console_issue_id
    if (issue.console_issue_id) {
      return issue.console_issue_id;
    }
    // Fallback to id if neither is present
    return issue.id;
  };

  // Helper to get the issue title regardless of schema
  const getIssueTitle = (issue) => {
    // First check for the normalized title field
    if (issue.title) {
      return issue.title;
    }

    // Platform API structure
    if (issue.visible_issue_content?.title) {
      return issue.visible_issue_content.title;
    }
    if (issue.created_issue_content?.title) {
      return issue.created_issue_content.title;
    }
    // Console API structure
    if (issue.issue_content?.title) {
      return issue.issue_content.title;
    }

    // Fallback
    return "Untitled Issue";
  };

  // When scan-level push checkbox is toggled
  const handleScanPushCheckbox = async (checked) => {
    if (checked) {
      // Reset the user changed flag when checkbox is first checked
      userChangedSelectionRef.current = false;

      // If issues are already loaded from RTK Query, select only open issues
      if (issues && issues.length > 0) {
        const openIssues = getOpenIssues();
        if (openIssues.length > 0) {
          const issueIds = openIssues.map((issue) => getIssueId(issue));
          setSelectedIssueIds(issueIds);
          onIssueSelectionChange && onIssueSelectionChange(scanId, openIssues);
        }
      } else {
        // If not loaded, expand first
        if (!isExpanded) {
          setIsExpanded(true);
        }

        // Then try to load issues via onExpand (for compatibility with parent component)
        if (onExpand) {
          try {
            const loadedIssues = await onExpand(scan);
            // Check if user manually changed selection before we got here
            if (userChangedSelectionRef.current) return;

            if (loadedIssues && loadedIssues.length > 0) {
              // Filter for open issues only
              const openIssues = loadedIssues.filter((issue) => {
                const status =
                  issue.status ||
                  issue.visible_issue_content?.status ||
                  issue.created_issue_content?.status ||
                  issue.issue_content?.status ||
                  "open";
                return status === "open";
              });
              if (openIssues.length > 0) {
                const issueIds = openIssues.map((issue) => getIssueId(issue));
                setSelectedIssueIds(issueIds);
                onIssueSelectionChange &&
                  onIssueSelectionChange(scanId, openIssues);
              }
            } else {
              // If onExpand didn't return issues, wait a bit for RTK Query to load them
              setTimeout(() => {
                // Skip if user has manually changed selection
                if (userChangedSelectionRef.current) return;
                const openIssues = getOpenIssues();
                if (openIssues.length > 0) {
                  const issueIds = openIssues.map((issue) => getIssueId(issue));
                  setSelectedIssueIds(issueIds);
                  onIssueSelectionChange &&
                    onIssueSelectionChange(scanId, openIssues);
                }
              }, 500);
            }
          } catch (error) {
            console.error("Failed to load issues for push selection:", error);
            // Still try to select from RTK Query data if available
            setTimeout(() => {
              // Skip if user has manually changed selection
              if (userChangedSelectionRef.current) return;
              const openIssues = getOpenIssues();
              if (openIssues.length > 0) {
                const issueIds = openIssues.map((issue) => getIssueId(issue));
                setSelectedIssueIds(issueIds);
                onIssueSelectionChange &&
                  onIssueSelectionChange(scanId, openIssues);
              }
            }, 500);
          }
        }
      }
    } else {
      // Unchecked: clear all selected issues and reset flag
      userChangedSelectionRef.current = false;
      setSelectedIssueIds([]);
      onIssueSelectionChange && onIssueSelectionChange(scanId, []);
    }
  };

  return (
    <li className="relative">
      {/* Scan Header */}
      <div
        className={`flex items-center px-3 py-2 hover:bg-surface-neutral cursor-pointer text-body-xs transition-colors ${
          isSelected ? "bg-accent-purple/5" : ""
        }`}
        onClick={handleSelect}
      >
        {/* Bulk select checkbox (push for scans; delete for Hub run-only rows) */}
        <div className="mr-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={selectedForPush}
            title={
              isActivityRunOnly
                ? "No linked scan — selectable for delete only (cannot push)"
                : undefined
            }
            onChange={async (e) => {
              onTogglePushSelect && onTogglePushSelect(scan, e.target.checked);
              // Run-only rows have no V1 issues to auto-select for push.
              if (!isActivityRunOnly) {
                await handleScanPushCheckbox(e.target.checked);
              }
            }}
            className="h-3.5 w-3.5 text-accent-purple border-border-default rounded cursor-pointer focus:ring-accent-purple/30"
          />
        </div>

        <button
          className="mr-2 text-text-tertiary hover:text-text-secondary focus:outline-none flex-shrink-0 transition-colors disabled:opacity-40"
          onClick={toggleExpand}
          disabled={isActivityRunOnly}
          title={
            isActivityRunOnly
              ? "No linked scan — open on Hub for the activity trace"
              : undefined
          }
        >
          {isExpanded ? (
            <FaChevronDown size={10} />
          ) : (
            <FaChevronRight size={10} />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <p className="text-body-xs font-medium text-text-primary truncate flex items-center gap-1.5">
            <span className="truncate">{scan.title}</span>
            {scan.activity_run_status === "canceled" ? (
              <span
                className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-800 border border-amber-200"
                title="Hub user stopped this run (partial results)">
                Stopped
              </span>
            ) : null}
          </p>
          <p className="text-caption text-text-tertiary">
            {isActivityRunOnly
              ? `Hub run · ${
                  scan.scan_id ? "scan missing" : "no scan ID"
                }${
                  scan.start_time || scan.updated_at
                    ? ` · ${formatDate(scan.start_time || scan.updated_at)}`
                    : ""
                }`
              : scan.start_time || scan.updated_at
                ? formatDate(scan.start_time || scan.updated_at)
                : null}
          </p>
        </div>

        {displayIssueCount > 0 && (
          <span className="text-caption font-semibold bg-surface-neutral-subtle text-text-secondary rounded-md px-1.5 py-0.5 min-w-[22px] text-center flex-shrink-0">
            {displayIssueCount}
          </span>
        )}
      </div>

      {/* Issues List */}
      {isExpanded && (
        <div className="pl-8 pr-3 pb-2 bg-surface-neutral/50">
          <div className="flex justify-between items-center mb-1.5">
            <div className="flex items-center gap-2">
              <button className="text-caption font-medium text-text-tertiary hover:text-accent-purple focus:outline-none transition-colors" onClick={selectOpenIssues} disabled={!issues || issues.length === 0}>
                Select Open
              </button>
              <button className="text-caption font-medium text-text-tertiary hover:text-accent-purple focus:outline-none transition-colors" onClick={areAllIssuesSelected() ? clearIssueSelection : selectAllIssues} disabled={!issues || issues.length === 0}>
                {areAllIssuesSelected() ? "Unselect All" : "Select All"}
              </button>
              <button className={`text-caption font-medium ${hasSelectedIssues() ? "text-text-tertiary hover:text-severity-high" : "text-text-tertiary/40 cursor-not-allowed"} focus:outline-none transition-colors`} onClick={handleDeleteClick} disabled={!hasSelectedIssues()}>
                Delete
              </button>
              <FaSearch
                className={`cursor-pointer transition-colors ${showSearch ? "text-accent-purple" : "text-text-tertiary hover:text-accent-purple"}`}
                onClick={(e) => { e.stopPropagation(); setShowSearch(!showSearch); }}
                title="Search findings"
                size={11}
              />
            </div>
            <span className="text-caption font-semibold text-text-tertiary">{displayIssueCount}</span>
          </div>
          {/* Search Input */}
          {showSearch && (
            <div className="mb-2">
              <input
                type="text"
                placeholder="Search findings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full text-caption px-2 py-1.5 bg-white border border-border-subtle rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-purple/20 placeholder:text-text-tertiary"
                autoFocus
              />
            </div>
          )}

          {issues && issues.length > 0 ? (
            <div className="space-y-3">
              {groupIssuesByStatus().map((group) => (
                <div key={group.status} className="">
                  {/* Status Header */}
                  <div
                    className={`px-2 py-1 rounded-t-md border-b text-xs font-medium flex items-center justify-between ${getStatusStyling(
                      group.status
                    )}`}
                  >
                    <span>
                      {formatStatusTitle(group.status)} ({group.issues.length})
                    </span>
                    <div className="flex items-center space-x-2">
                      {/* Select/Deselect button for this status group */}
                      {group.issues.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStatusGroupSelection(
                              group.status,
                              group.issues
                            );
                          }}
                          className="text-caption font-medium text-accent-purple hover:underline focus:outline-none"
                          title={
                            areAllStatusIssuesSelected(group.issues)
                              ? `Deselect all ${group.status} issues`
                              : `Select all ${group.status} issues`
                          }
                        >
                          {areAllStatusIssuesSelected(group.issues)
                            ? "Deselect"
                            : "Select"}
                        </button>
                      )}
                      {onDeleteAllIssuesByStatus && group.issues.length > 0 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteAllIssuesByStatus(
                              scan,
                              group.status,
                              group.issues
                            );
                          }}
                          className="text-caption font-medium text-severity-high hover:underline focus:outline-none"
                          title={`Delete all ${group.status} issues`}
                        >
                          Delete All
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Issues in this status */}
                  <ul className="space-y-1 bg-white border-l border-r border-b border-border-subtle rounded-b-lg">
                    {group.issues.map((issue) => {
                      const issueId = getIssueId(issue);
                      return (
                        <li
                          key={issueId}
                          className="flex items-center py-1 px-2 text-body-xs hover:bg-surface-neutral transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedIssueIds.includes(issueId)}
                            onChange={(e) => {
                              e.stopPropagation();
                              toggleIssueSelection(issueId);
                            }}
                            className="h-3.5 w-3.5 mr-2 text-accent-purple border-border-default rounded cursor-pointer focus:ring-accent-purple/30 flex-shrink-0"
                          />
                          <span
                            className="truncate"
                            title={getIssueTitle(issue)}
                          >
                            {getIssueTitle(issue)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-caption text-text-tertiary italic">
              {isLoadingIssues ? "Loading findings..." : "No findings"}
            </p>
          )}
        </div>
      )}

      {/* Delete Confirmation and Progress Modal */}
      <Transition appear show={showDeleteModal} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (!deleteInProgress) {
              setShowDeleteModal(false);
            }
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      {deleteProgress.isComplete ? (
                        deleteProgress.failed > 0 ? (
                          <XCircleIcon
                            className="h-6 w-6 text-red-600"
                            aria-hidden="true"
                          />
                        ) : (
                          <CheckCircleIcon
                            className="h-6 w-6 text-green-600"
                            aria-hidden="true"
                          />
                        )
                      ) : (
                        <ExclamationTriangleIcon
                          className="h-6 w-6 text-red-600"
                          aria-hidden="true"
                        />
                      )}
                    </div>
                    <div className="ml-3 w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-medium leading-6 text-gray-900"
                      >
                        {deleteProgress.isComplete
                          ? deleteProgress.failed > 0
                            ? "Deletion Completed with Errors"
                            : "Deletion Completed Successfully"
                          : "Delete Selected Issues"}
                      </Dialog.Title>

                      <div className="mt-2">
                        {!deleteInProgress ? (
                          <p className="text-sm text-gray-500">
                            Are you sure you want to delete{" "}
                            {selectedIssueIds.length} selected issue
                            {selectedIssueIds.length !== 1 ? "s" : ""}? This
                            action cannot be undone.
                          </p>
                        ) : (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-500">
                              {deleteProgress.isComplete
                                ? `Completed: ${
                                    deleteProgress.success
                                  } deleted${
                                    deleteProgress.failed > 0
                                      ? `, ${deleteProgress.failed} failed`
                                      : ""
                                  } out of ${deleteProgress.total} issues`
                                : `Deleting issues... ${deleteProgress.current}/${deleteProgress.total}`}
                            </p>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  deleteProgress.isComplete
                                    ? deleteProgress.failed > 0
                                      ? "bg-yellow-500"
                                      : "bg-green-500"
                                    : "bg-blue-500"
                                }`}
                                style={{
                                  width: `${
                                    deleteProgress.total > 0
                                      ? (deleteProgress.current /
                                          deleteProgress.total) *
                                        100
                                      : 0
                                  }%`,
                                }}
                              ></div>
                            </div>

                            {/* Success/Fail Counts */}
                            {deleteProgress.isComplete && (
                              <div className="flex justify-between text-xs text-gray-600">
                                <span className="text-green-600">
                                  ✓ Success: {deleteProgress.success}
                                </span>
                                {deleteProgress.failed > 0 && (
                                  <span className="text-red-600">
                                    ✗ Failed: {deleteProgress.failed}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Error Message */}
                        {deleteError && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                            {deleteError}
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex justify-end space-x-3">
                        {!deleteInProgress ? (
                          <>
                            <button
                              type="button"
                              className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                              onClick={() => setShowDeleteModal(false)}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                              onClick={handleDeleteSelectedIssues}
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className={`inline-flex justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                              deleteProgress.isComplete
                                ? "bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-500"
                                : "bg-gray-100 text-gray-400 cursor-not-allowed"
                            }`}
                            onClick={
                              deleteProgress.isComplete
                                ? () => setShowDeleteModal(false)
                                : undefined
                            }
                            disabled={!deleteProgress.isComplete}
                          >
                            {deleteProgress.isComplete
                              ? "Close"
                              : "Deleting..."}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </li>
  );
};

export default ScanListItem;
